import { useState } from 'react'
import { Loader2, Phone, Search, ShoppingCart } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  useSearchAvailableNumbers,
  usePurchaseNumber,
  type AvailableNumber,
  type NumberCountry,
  type NumberType,
} from '@/api/twilioNumbers'
import { UpdateCardDialog } from '@/components/manager/subscription/UpdateCardDialog'

interface Props {
  open: boolean
  onClose: () => void
  companyId: number
}

export function NumberMarketplaceDialog({ open, onClose, companyId }: Props) {
  const { t } = useTranslation()

  const [country, setCountry] = useState<NumberCountry>('CA')
  const [type, setType] = useState<NumberType>('local')
  const [areaCode, setAreaCode] = useState('')
  const [contains, setContains] = useState('')
  const [limit, setLimit] = useState(10)
  const [purchasingNumber, setPurchasingNumber] = useState<string | null>(null)
  const [showAddCard, setShowAddCard] = useState(false)

  const { data: results = [], isFetching, refetch } = useSearchAvailableNumbers(
    { country, type, areaCode, contains, limit },
    open,
  )

  const purchase = usePurchaseNumber()

  const handleBuy = async (num: AvailableNumber) => {
    setPurchasingNumber(num.phoneNumber)
    try {
      await purchase.mutateAsync({
        phoneNumber: num.phoneNumber,
        companyId,
      })
      toast.success(
        t('manager.numbers.marketplace.purchaseSuccess', {
          number: num.phoneNumber,
        }),
      )
      onClose()
    } catch (err: unknown) {
      const e = err as {
        response?: {
          status?: number
          data?: { error?: string; detail?: string; message?: string }
        }
      }
      const status = e.response?.status
      const code = e.response?.data?.error
      // The server sends a code in `error` and the readable sentence in `message`.
      const serverMsg =
        e.response?.data?.message || e.response?.data?.detail || code

      if (code === 'number_unavailable') {
        // Taken in the meantime, or the carrier refused: show fresh numbers (the
        // server then offers the other carrier's).
        toast.error(t('manager.numbers.marketplace.numberTaken'))
        void refetch()
      } else if (status === 202) {
        toast.error(t('manager.numbers.marketplace.subscriptionRequired'))
      } else if (code === 'payment_method_required' || status === 402) {
        // Essai sans carte : on ouvre directement l'ajout de carte in-app.
        toast.error(
          t(
            'manager.numbers.marketplace.paymentMethodRequired',
            'Ajoutez une carte pour acquérir un numéro. Aucun montant ne sera prélevé pendant votre essai.',
          ),
        )
        setShowAddCard(true)
      } else if (status === 403) {
        toast.error(t('manager.numbers.marketplace.trialLimit'))
      } else if (serverMsg) {
        toast.error(serverMsg)
      } else {
        toast.error(t('manager.numbers.marketplace.purchaseError'))
      }
    } finally {
      setPurchasingNumber(null)
    }
  }

  const handleOpenChange = (next: boolean) => {
    if (!next && !purchase.isPending) onClose()
  }

  return (
    <>
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{t('manager.numbers.marketplace.title')}</DialogTitle>
          <DialogDescription>
            {t('manager.numbers.marketplace.description')}
          </DialogDescription>
        </DialogHeader>

        {/* Filtres */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-slate-500 uppercase">
              {t('manager.numbers.marketplace.country')}
            </Label>
            <Select
              value={country}
              onValueChange={(v) => setCountry(v as NumberCountry)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="CA">🇨🇦 Canada</SelectItem>
                <SelectItem value="US">🇺🇸 United States</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-slate-500 uppercase">
              {t('manager.numbers.marketplace.type')}
            </Label>
            <Select
              value={type}
              onValueChange={(v) => setType(v as NumberType)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="local">
                  {t('manager.numbers.marketplace.local')}
                </SelectItem>
                <SelectItem value="tollfree">
                  {t('manager.numbers.marketplace.tollFree')}
                </SelectItem>
              </SelectContent>
            </Select>
            {type === 'tollfree' && (
              <p className="text-xs text-amber-700 dark:text-amber-300 leading-relaxed">
                {t('manager.numbers.tollfreeSms.choiceNote')}
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-slate-500 uppercase">
              {t('manager.numbers.marketplace.areaCode')}
            </Label>
            <Input
              value={areaCode}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, '').slice(0, 3)
                setAreaCode(val)
              }}
              placeholder="514"
              inputMode="numeric"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-slate-500 uppercase">
              {t('manager.numbers.marketplace.contains')}
            </Label>
            <Input
              value={contains}
              onChange={(e) => setContains(e.target.value)}
              placeholder="CALL"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-slate-500 uppercase">
              {t('manager.numbers.marketplace.limit')}
            </Label>
            <Select
              value={String(limit)}
              onValueChange={(v) => setLimit(Number(v))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Résultats */}
        <div className="max-h-[50vh] overflow-y-auto -mx-2">
          {isFetching ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-5 w-5 animate-spin text-slate-400 mr-2" />
              <span className="text-sm text-slate-500">
                {t('manager.numbers.marketplace.searching')}
              </span>
            </div>
          ) : results.length === 0 ? (
            <div className="text-center py-12">
              <Search className="h-10 w-10 text-slate-200 dark:text-slate-700 mx-auto mb-3" />
              <p className="text-sm font-medium text-slate-900 dark:text-white mb-1">
                {t('manager.numbers.marketplace.noResults')}
              </p>
              <p className="text-xs text-slate-400 dark:text-slate-500">
                {t('manager.numbers.marketplace.noResultsDescription')}
              </p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-800 text-left">
                  <th className="px-2 py-2 font-semibold text-xs uppercase text-slate-500">
                    {t('manager.numbers.marketplace.number')}
                  </th>
                  <th className="hidden md:table-cell px-2 py-2 font-semibold text-xs uppercase text-slate-500">
                    {t('manager.numbers.marketplace.locality')}
                  </th>
                  <th className="hidden sm:table-cell px-2 py-2 font-semibold text-xs uppercase text-slate-500">
                    {t('manager.numbers.marketplace.region')}
                  </th>
                  <th className="px-2 py-2 text-right" />
                </tr>
              </thead>
              <tbody>
                {results.map((num) => {
                  const isBuying = purchasingNumber === num.phoneNumber
                  return (
                    <tr
                      key={num.phoneNumber}
                      className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50"
                    >
                      <td className="px-2 py-3">
                        <div className="flex items-center gap-2">
                          <Phone className="h-3.5 w-3.5 text-slate-400" />
                          <span className="font-mono font-semibold text-slate-900 dark:text-white">
                            {num.phoneNumber}
                          </span>
                        </div>
                      </td>
                      <td className="hidden md:table-cell px-2 py-3 text-slate-600 dark:text-slate-300">
                        {num.locality || '—'}
                      </td>
                      <td className="hidden sm:table-cell px-2 py-3 text-slate-600 dark:text-slate-300">
                        {num.region || '—'}
                      </td>
                      <td className="px-2 py-3 text-right">
                        <Button
                          size="sm"
                          onClick={() => void handleBuy(num)}
                          disabled={!!purchasingNumber || purchase.isPending}
                          className="bg-[#0A192F] text-white hover:bg-[#0A192F]/90 dark:bg-white dark:text-[#0A192F]"
                        >
                          {isBuying ? (
                            <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                          ) : (
                            <ShoppingCart className="h-3.5 w-3.5 mr-1.5" />
                          )}
                          {t('manager.numbers.marketplace.buy')}
                        </Button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>
      </DialogContent>
    </Dialog>

    <UpdateCardDialog
      open={showAddCard}
      onClose={() => setShowAddCard(false)}
      description={t(
        'manager.numbers.marketplace.addCardDescription',
        "Aucun montant ne sera prélevé pendant votre essai.",
      )}
    />
    </>
  )
}
